'use client'

import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'

import { waitForFreestarConsent } from './consent'
import { ensureFreestarGlobal, invokeFreestarSlot } from './runtime-state'
import { trackFreestarEvent } from './telemetry'
import type { FreestarOpportunity, FreestarSlotConfig } from './types'

const FREESTAR_SCRIPT_SRC = 'https://a.pub.network/giphy-com/pubfig.min.js'
const FREESTAR_CLS_CSS = 'https://a.pub.network/giphy-com/cls.css'
const FREESTAR_PRECONNECTS = [
    'https://a.pub.network',
    'https://b.pub.network',
    'https://c.pub.network',
    'https://d.pub.network',
    'https://btloader.com',
    'https://api.btloader.com',
]

function FreestarSlot({ opportunity, slot }: { opportunity: FreestarOpportunity; slot: FreestarSlotConfig }) {
    const invoked = useRef(false)

    useEffect(() => {
        if (invoked.current) {
            return
        }
        invoked.current = true
        try {
            invokeFreestarSlot(slot)
            trackFreestarEvent({ opportunity, state: 'slot_invoked', slot })
        } catch (error) {
            trackFreestarEvent({ opportunity, state: 'runtime_error', reason: 'runtime_error', slot })
        }
    }, [opportunity, slot])

    if (slot.kind !== 'leaderboard_atf') {
        return null
    }

    return (
        <div className="mx-auto hidden min-h-[90px] w-full justify-center md:flex">
            <div
                id={slot.slotId}
                data-freestar-ad={slot.dataFreestarAd}
                style={{
                    minHeight: slot.reservedHeight,
                    minWidth: slot.reservedWidth,
                }}
            />
        </div>
    )
}

function FreestarRuntime({ opportunity }: { opportunity: Extract<FreestarOpportunity, { status: 'ready' }> }) {
    const [consentAllowed, setConsentAllowed] = useState(false)
    const trackedScriptLoaded = useRef(false)

    useEffect(() => {
        let active = true
        ensureFreestarGlobal()
        trackFreestarEvent({ opportunity, state: 'attempt' })

        waitForFreestarConsent().then((consent) => {
            if (!active) {
                return
            }
            if (consent.status === 'allowed') {
                setConsentAllowed(true)
                return
            }
            trackFreestarEvent({ opportunity, state: consent.reason, reason: consent.reason })
        })

        return () => {
            active = false
        }
    }, [opportunity])

    if (!consentAllowed) {
        return null
    }

    return (
        <>
            {FREESTAR_PRECONNECTS.map((href) => (
                <link key={href} rel="preconnect" href={href} />
            ))}
            <link rel="stylesheet" href={FREESTAR_CLS_CSS} />
            <Script
                id="freestar-pubfig"
                src={FREESTAR_SCRIPT_SRC}
                strategy="afterInteractive"
                onReady={() => {
                    ensureFreestarGlobal().initCallbackCalled = true
                    if (!trackedScriptLoaded.current) {
                        trackedScriptLoaded.current = true
                        trackFreestarEvent({ opportunity, state: 'script_loaded' })
                    }
                }}
                onError={() => {
                    trackFreestarEvent({ opportunity, state: 'script_error', reason: 'script_error' })
                }}
            />
            {opportunity.slots.map((slot) => (
                <FreestarSlot key={slot.slotId} opportunity={opportunity} slot={slot} />
            ))}
        </>
    )
}

export default function FreestarPlacement({ opportunity }: { opportunity?: FreestarOpportunity }) {
    const trackedSuppression = useRef<string | undefined>(undefined)

    useEffect(() => {
        if (!opportunity || opportunity.status !== 'suppressed') {
            return
        }
        if (opportunity.reason === 'backend_not_cleared' || opportunity.reason === 'runtime_disabled') {
            return
        }
        const key = `${opportunity.context.view}:${opportunity.context.deviceType}:${opportunity.reason}`
        if (trackedSuppression.current === key) {
            return
        }
        trackedSuppression.current = key
        trackFreestarEvent({ opportunity, state: opportunity.reason, reason: opportunity.reason })
    }, [opportunity])

    if (!opportunity || opportunity.status !== 'ready') {
        return null
    }

    return <FreestarRuntime opportunity={opportunity} />
}
